import { majorAirports, calculateDistance, getTimezoneOffsetHours, getAirport } from './airports';

/**
 * Flight calculation utilities that properly account for timezone differences.
 * 
 * KEY FORMULA:
 * Landing Local Time = Departure Local Time + Flight Duration + Timezone Offset
 * 
 * Example: DFW (UTC-6) → AUH (UTC+4)
 * - Depart DFW: 8:04 PM (1204 min from midnight)
 * - Flight Duration: ~17 hours (1012 min)
 * - Timezone Offset: +10 hours (AUH is 10 hours ahead of DFW)
 * - Landing Time = 1204 + 1012 + 600 = 2816 min = landing at 22:56 (10:56 PM) NEXT DAY
 * 
 * The "+10 hours" is because when it's noon in Dallas, it's 10 PM in Abu Dhabi.
 */

export interface FlightTimeResult {
  /** Flight duration in minutes (actual air time) */
  flightDurationMinutes: number;
  /** Timezone offset in minutes (positive = dest ahead of origin) */
  timezoneOffsetMinutes: number;
  /** Total elapsed local time from departure to landing (duration + tz offset) */
  elapsedLocalMinutes: number;
  /** Hours component of flight duration */
  durationHours: number;
  /** Minutes component of flight duration */
  durationMinutes: number;
  /** Formatted duration string like "16h 52m" */
  durationFormatted: string;
}

/**
 * Calculate flight duration and timezone offset between two airports.
 */
export function calculateFlightTime(originCode: string, destCode: string): FlightTimeResult | null {
  const origin = getAirport(originCode);
  const dest = getAirport(destCode);
  if (!origin || !dest) return null;
  
  // Calculate distance and flight time
  const distance = calculateDistance(origin.lat, origin.lng, dest.lat, dest.lng);
  const cruiseTimeHours = distance / 500; // ~500 mph average
  const flightDurationMinutes = Math.round(cruiseTimeHours * 60) + 45; // Add 45 min for takeoff/landing
  
  // Timezone offset (in minutes)
  const timezoneOffsetHours = getTimezoneOffsetHours(originCode, destCode);
  const timezoneOffsetMinutes = Math.round(timezoneOffsetHours * 60);
  
  // Elapsed local time = flight duration + timezone shift
  const elapsedLocalMinutes = flightDurationMinutes + timezoneOffsetMinutes;
  
  const durationHours = Math.floor(flightDurationMinutes / 60);
  const durationMinutes = flightDurationMinutes % 60;
  const durationFormatted = durationMinutes > 0 
    ? `${durationHours}h ${durationMinutes}m` 
    : `${durationHours}h`;
  
  return {
    flightDurationMinutes,
    timezoneOffsetMinutes,
    elapsedLocalMinutes,
    durationHours,
    durationMinutes,
    durationFormatted,
  };
}

export interface DepartureArrivalTime {
  /** Departure time in minutes from midnight (origin local time) */
  departureMinutes: number;
  /** Landing time in minutes from midnight (destination local time) */
  landingMinutes: number;
  /** Number of days added (0 = same day, 1 = next day, -1 = previous day if going west) */
  dayOffset: number;
  /** Date string for display (e.g., "Jan 30" or "Jan 29 +1") */
  landingDayLabel: string;
}

/**
 * Given a departure time (in origin local minutes from midnight),
 * calculate when the flight lands in destination local time.
 * 
 * @param departureMinutes - Departure time in minutes from midnight (origin timezone)
 * @param flightTime - Flight time result from calculateFlightTime
 * @returns Landing time details
 */
export function calculateLandingTime(
  departureMinutes: number,
  flightTime: FlightTimeResult
): DepartureArrivalTime {
  // Landing = Departure + Flight Duration + Timezone Offset
  const rawLandingMinutes = departureMinutes + flightTime.elapsedLocalMinutes;
  
  // Normalize to get the day offset
  let dayOffset = 0;
  let landingMinutes = rawLandingMinutes;
  
  while (landingMinutes >= 1440) {
    landingMinutes -= 1440;
    dayOffset += 1;
  }
  while (landingMinutes < 0) {
    landingMinutes += 1440;
    dayOffset -= 1;
  }
  
  const landingDayLabel = dayOffset === 0 ? '' : dayOffset > 0 ? ` (+${dayOffset})` : ` (${dayOffset})`;
  
  return {
    departureMinutes,
    landingMinutes,
    dayOffset,
    landingDayLabel,
  };
}

/**
 * Given a required landing time (destination local), calculate when to depart (origin local).
 * Works backwards from landing to find departure.
 * 
 * @param landingMinutes - Required landing time in minutes from midnight (destination timezone)
 * @param flightTime - Flight time result from calculateFlightTime
 * @returns Departure time in minutes from midnight (origin timezone), may be negative (previous day)
 */
export function calculateDepartureTime(
  landingMinutes: number,
  flightTime: FlightTimeResult
): { departureMinutes: number; dayOffset: number } {
  // Departure = Landing - Flight Duration - Timezone Offset
  const rawDepartureMinutes = landingMinutes - flightTime.elapsedLocalMinutes;
  
  // Calculate day offset
  let dayOffset = 0;
  let departureMinutes = rawDepartureMinutes;
  
  while (departureMinutes >= 1440) {
    departureMinutes -= 1440;
    dayOffset += 1;
  }
  // Keep negative values for previous day departures (e.g., -120 = 10 PM previous day)
  while (departureMinutes < -1440) {
    departureMinutes += 1440;
    dayOffset -= 1;
  }
  
  // If departure is negative but within one day, it means previous day
  if (departureMinutes < 0) {
    dayOffset = -1;
    // departureMinutes stays negative to indicate previous day time
    // e.g., -120 means 22:00 (10 PM) previous day
  }
  
  return { departureMinutes, dayOffset };
}

/**
 * Format minutes from midnight to readable time string.
 * Handles negative (previous day) and >1440 (next day) values.
 */
export function formatMinutesToTime(minutes: number): string {
  let displayMinutes = minutes;
  let suffix = '';
  
  if (minutes >= 1440) {
    displayMinutes = minutes % 1440;
    const daysAhead = Math.floor(minutes / 1440);
    suffix = ` (+${daysAhead})`;
  } else if (minutes < 0) {
    // Convert to previous day time (e.g., -120 → 22:00 previous day)
    displayMinutes = 1440 + (minutes % 1440);
    if (displayMinutes >= 1440) displayMinutes -= 1440;
    const daysBehind = Math.ceil(Math.abs(minutes) / 1440);
    suffix = daysBehind === 1 ? '' : ` (-${daysBehind})`;
  }
  
  const h = Math.floor(displayMinutes / 60) % 24;
  const m = displayMinutes % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  
  return `${displayH}:${m.toString().padStart(2, '0')} ${period}${suffix}`;
}

/**
 * Determine if same-day arrival is possible for an event, accounting for timezones.
 * 
 * @param originCode - Origin airport code
 * @param destCode - Destination airport code
 * @param eventStartMinutes - Event start time in minutes from midnight (destination local)
 * @param driveToVenueMinutes - Drive time from airport to venue
 * @returns Analysis of whether same-day arrival is feasible
 */
export function analyzeSameDayArrival(
  originCode: string,
  destCode: string,
  eventStartMinutes: number,
  driveToVenueMinutes: number
): {
  canArriveSameDay: boolean;
  useRedEyeInbound: boolean;
  latestLandingMinutes: number;
  requiredDepartureMinutes: number;
  departureDay: 'same' | 'previous' | 'two-days-before';
  flightTime: FlightTimeResult | null;
} {
  const flightTime = calculateFlightTime(originCode, destCode);
  if (!flightTime) {
    return {
      canArriveSameDay: false,
      useRedEyeInbound: false,
      latestLandingMinutes: 0,
      requiredDepartureMinutes: 0,
      departureDay: 'previous',
      flightTime: null,
    };
  }
  
  // Required prep before event (all times in destination local)
  const SETUP_TIME = 120; // 2 hours soundcheck/setup
  const FRESHEN_UP = 20;
  const MEAL_TIME = 20;
  const DRIVE_TO_VENUE = 5; // Hotel to venue
  const AIRPORT_PROCESSING = 45; // Baggage/customs
  const HOTEL_CHECKIN = 20;
  
  // Work backwards from event start to find latest landing time
  const mustBeAtVenue = eventStartMinutes - SETUP_TIME;
  const leaveHotel = mustBeAtVenue - DRIVE_TO_VENUE;
  const startGettingReady = leaveHotel - MEAL_TIME - FRESHEN_UP;
  const arriveHotel = startGettingReady - HOTEL_CHECKIN;
  const arriveAirport = arriveHotel - driveToVenueMinutes;
  const latestLandingMinutes = arriveAirport - AIRPORT_PROCESSING;
  
  // Calculate required departure time (in origin local time)
  const { departureMinutes: rawDepartureMinutes } = calculateDepartureTime(latestLandingMinutes, flightTime);
  
  // Feasibility rules:
  // - Departure after 6 AM same day = same-day travel
  // - Departure between 4 PM previous day and 6 AM = red-eye inbound (overnight flight TO event)
  // - Departure before 4 PM previous day = need day-before arrival (too early for red-eye)
  
  const EARLIEST_SAME_DAY_DEPARTURE = 6 * 60; // 6 AM
  const EARLIEST_REDEYE_DEPARTURE = -8 * 60; // 4 PM previous day = -480 minutes
  
  const canArriveSameDay = rawDepartureMinutes >= EARLIEST_REDEYE_DEPARTURE;
  const useRedEyeInbound = rawDepartureMinutes < EARLIEST_SAME_DAY_DEPARTURE && rawDepartureMinutes >= EARLIEST_REDEYE_DEPARTURE;
  
  let departureDay: 'same' | 'previous' | 'two-days-before';
  if (rawDepartureMinutes >= EARLIEST_SAME_DAY_DEPARTURE) {
    departureDay = 'same';
  } else if (rawDepartureMinutes >= EARLIEST_REDEYE_DEPARTURE) {
    departureDay = 'previous';
  } else {
    departureDay = 'two-days-before';
  }
  
  return {
    canArriveSameDay,
    useRedEyeInbound,
    latestLandingMinutes,
    requiredDepartureMinutes: rawDepartureMinutes,
    departureDay,
    flightTime,
  };
}

/**
 * Calculate the complete arrival timeline with proper timezone handling.
 * Returns times in destination local time.
 */
export function calculateArrivalTimeline(
  originCode: string,
  destCode: string,
  departureMinutes: number,  // Origin local time
  driveToVenueMinutes: number,
  eventStartMinutes: number  // Destination local time
): {
  landingMinutes: number;
  landingDayOffset: number;
  baggageCompleteMinutes: number;
  arriveHotelMinutes: number;
  freshenUpCompleteMinutes: number;
  mealCompleteMinutes: number;
  leaveHotelMinutes: number;
  arriveVenueMinutes: number;
  timeline: Array<{ time: number; label: string; icon: string }>;
} | null {
  const flightTime = calculateFlightTime(originCode, destCode);
  if (!flightTime) return null;
  
  const BAGGAGE_TIME = 45;
  const HOTEL_CHECKIN = 20;
  const FRESHEN_UP = 20;
  const MEAL_TIME = 20;
  const DRIVE_TO_VENUE = 5;
  const SETUP_TIME = 120;
  
  // Calculate landing time
  const landing = calculateLandingTime(departureMinutes, flightTime);
  
  // All subsequent times are in destination local time
  const baggageCompleteMinutes = landing.landingMinutes + BAGGAGE_TIME;
  const arriveHotelMinutes = baggageCompleteMinutes + driveToVenueMinutes;
  const checkinCompleteMinutes = arriveHotelMinutes + HOTEL_CHECKIN;
  const freshenUpCompleteMinutes = checkinCompleteMinutes + FRESHEN_UP;
  const mealCompleteMinutes = freshenUpCompleteMinutes + MEAL_TIME;
  const leaveHotelMinutes = mealCompleteMinutes;
  const arriveVenueMinutes = leaveHotelMinutes + DRIVE_TO_VENUE;
  
  return {
    landingMinutes: landing.landingMinutes,
    landingDayOffset: landing.dayOffset,
    baggageCompleteMinutes,
    arriveHotelMinutes,
    freshenUpCompleteMinutes,
    mealCompleteMinutes,
    leaveHotelMinutes,
    arriveVenueMinutes,
    timeline: [
      { time: landing.landingMinutes, label: `Land at ${destCode}`, icon: 'plane' },
      { time: baggageCompleteMinutes, label: `Baggage/customs (${BAGGAGE_TIME} min)`, icon: 'baggage' },
      { time: arriveHotelMinutes, label: `Arrive hotel • Drive (${driveToVenueMinutes} min)`, icon: 'car' },
      { time: checkinCompleteMinutes, label: `Check-in (${HOTEL_CHECKIN} min)`, icon: 'hotel' },
      { time: freshenUpCompleteMinutes, label: `Freshen up (${FRESHEN_UP} min)`, icon: 'shirt' },
      { time: mealCompleteMinutes, label: `Meal (${MEAL_TIME} min)`, icon: 'utensils' },
      { time: leaveHotelMinutes, label: `Leave hotel for venue (${DRIVE_TO_VENUE} min)`, icon: 'car' },
      { time: arriveVenueMinutes, label: `Arrive venue • Soundcheck/Setup (${SETUP_TIME} min)`, icon: 'map' },
      { time: eventStartMinutes, label: 'Performance starts', icon: 'music' },
    ],
  };
}

/**
 * Calculate departure timeline after event ends.
 */
export function calculateDepartureTimeline(
  originCode: string,  // Event location (where we're departing FROM)
  destCode: string,    // Home (where we're flying TO)
  eventEndMinutes: number,  // Local time at event
  driveToAirportMinutes: number
): {
  packUpCompleteMinutes: number;
  arriveAirportMinutes: number;
  soonestDepartureMinutes: number;
  isNextDayDeparture: boolean;
  timeline: Array<{ time: number; label: string; icon: string }>;
} {
  const PACK_UP_TIME = 30;
  const AIRPORT_BUFFER = 120;
  
  const packUpCompleteMinutes = eventEndMinutes + PACK_UP_TIME;
  const arriveAirportMinutes = packUpCompleteMinutes + driveToAirportMinutes;
  const soonestDepartureMinutes = arriveAirportMinutes + AIRPORT_BUFFER;
  
  // Red-eye departures allowed up to 6 AM next day (1440 + 360 = 1800)
  const LATEST_REDEYE = 1800;
  const isNextDayDeparture = soonestDepartureMinutes > LATEST_REDEYE;
  
  return {
    packUpCompleteMinutes,
    arriveAirportMinutes,
    soonestDepartureMinutes,
    isNextDayDeparture,
    timeline: [
      { time: eventEndMinutes, label: 'Performance ends', icon: 'check' },
      { time: packUpCompleteMinutes, label: `Pack up, eat, say goodbyes (${PACK_UP_TIME} min)`, icon: 'timer' },
      { time: arriveAirportMinutes, label: `Drive to airport (${driveToAirportMinutes} min)`, icon: 'car' },
      { time: soonestDepartureMinutes, label: `Airport buffer (${AIRPORT_BUFFER} min)`, icon: 'clock' },
    ],
  };
}
